import { html, nothing } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import "../address/address-form";
import { CheckoutStep, CheckoutStepTemplate } from "./checkout-step";

import { connect } from "pwa-helpers";
import { StoreState } from "../../store/store.interface";
import { calculateFreight, getFreightCalculationError, getFreightCalculationResponse, getFreightCalculationStatus, getPoOrderNumber, getShippingAddress, isPurchaseOrderApplied, isShippingAddressValid, updateShippingAddress, updateSkipFreightCalculation } from "./shop-checkout";
import store, { waitForAddressValidation, waitForFreightCalculation } from "../../store/store";
import { getUserDataForUserAnalytics } from "../../features/user-profile/user-profile.selectors";
import { getDiscount } from "../../features/promo-code/promo-code.selectors";
import { getTax } from "../../features/estimated-tax/estimated-tax.selectors";
import { booleanConverter } from "../../common/converters";
import { ActionStatus } from "../../common/constants/action-status";
import { handleApiError } from "../../common/utils/api-utils";
import { getCountryStatus } from "../country-selector/country";
import { getCountryStateStatus } from "../country-selector/country-state";
import { RmsBookstoreAddress } from "./shop-checkout.interface";
import { isAddressValid } from "../../common/utils/address-util";
import { AddressForm } from "../address/address-form";
import { loading } from "../../common/constants/api-constants";
import { getCartForAnalytics } from "../cart/shop-cart/store/shop-cart.selectors";
import emit from "../../common/events";
import { addAnalyticsCheckoutFlowMetadata } from "../../common/utils/cart-util";
import { getProductsWithAdditionalAnalytics } from "../../common/utils/analytics-util";

/**
 * Checkout Step to capture user shipping address and save it in redux shop checkout state.
 */
@customElement('mhecomm-shipping-address-checkout-step')
export class ShippingAddressCheckoutStep extends connect(store)(CheckoutStep) implements CheckoutStepTemplate{

  @state()
  private showButton = false;

  @property({ converter: booleanConverter })
  showSchoolFreightCostMessage = false;

  @property({ converter: booleanConverter })
  skipFreightCalculation = false;
  
  @state()
  private readonly type = "shipping";

  @state()
  private focusOnFirstField = false;

  private hasAddressFormLoadedListener = false;

  private readonly onAddressFormLoaded = () => { this.showButton = true; };

  stateChanged(state: StoreState) {
    this.canEditStep(state);

    if (getFreightCalculationStatus(state) === ActionStatus.failed) {
      handleApiError(getFreightCalculationError(state));
      return;
    }
    if(getCountryStatus(state) === ActionStatus.failed || getCountryStateStatus(state) === ActionStatus.failed){
      this.showButton = false;
      return;
    }
  }

  /**
   * @returns template of shipping address form.
   */
  renderCheckoutStep() {
    return html`
      ${this.showSchoolFreightCostMessage ?
        html`<p><small><strong>For District/School Purchasers:</strong> to ensure you get the right freight cost, please select an existing address for your school. New addresses may not get special freight.</small></p>`
        : nothing
      }
      <mhecomm-address-form
        id="shipping-address-form"
        type="${this.type}"
        ?focusOnFirstField=${this.focusOnFirstField}
        shouldfilterpoboxaddress>
      </mhecomm-address-form>
      ${this.showButton ? this.renderNextStepButton() : nothing}
    `;
  }

  async forceCompleteStep() {
    if(!isShippingAddressValid(store.getState() as StoreState)){
      this.errorMessage = "Shipping address received from PayPal is not a valid address. Please edit your shipping address.";
    }
    this.isLoading = false;
  }

  loadStep() {
    this.isLoading = false;
    if (!this.hasAddressFormLoadedListener) {
      this.addTrackedEventListener(window, 'address-form-loaded', this.onAddressFormLoaded);
      this.hasAddressFormLoadedListener = true;
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.hasAddressFormLoadedListener = false;
  }

  /**
   * On click continue to next checkout step, validate that user has entered a valid shipping address.
   * If address is international, validate using international address validation API.
   * If US address, validate using US address validation API.
   * If address is valid, store valid address in Shop Checkout redux state.
   */
  async stepDoValidation() {
    await waitForAddressValidation();
    await waitForFreightCalculation();
    const addressForm = this.getAddressForm();
    const currentAddress: RmsBookstoreAddress | undefined = getShippingAddress(store.getState() as StoreState);
    const isFreightAlreadyCalculated = getFreightCalculationResponse(store.getState() as StoreState) && this.addressNotChanged(currentAddress, addressForm);
    if(!isFreightAlreadyCalculated){
      emit({
        element: this,
        name: 'mhecomm-shipping-method-step-invalidate',
        detail: {},
      });
    }
    store.dispatch(updateSkipFreightCalculation(isFreightAlreadyCalculated));
    if (addressForm) {
      await addressForm.validate().then(addressValidationInfo => {
        if(addressValidationInfo && isAddressValid(addressValidationInfo)){
          store.dispatch(updateShippingAddress(addressValidationInfo));
        }
        else {
          throw new Error("Please review the errors highlighted below before resubmitting.")
        }
      })
    }
    if (!this.skipFreightCalculation && !isFreightAlreadyCalculated){
      await store.dispatch(calculateFreight()).unwrap();
    }
  }

  addressNotChanged(currentAddress?: RmsBookstoreAddress, addressForm?: AddressForm) {
    return !!currentAddress && !!addressForm && isAddressValid(currentAddress) &&
      currentAddress.address1 === addressForm.streetAddress &&
      currentAddress.zipCode === addressForm.zipCode &&
      currentAddress.city === addressForm.city &&
      currentAddress.state === addressForm.countryState &&
      currentAddress.country === addressForm.countryCode &&
      (!currentAddress.address2 || currentAddress.address2 === addressForm.unit)
  }

  /**
   * On edit checkout step, prepopulate shipping address from redux shop checkout state.
   * Invalidate selected shipping method and shipping address in redux.
   */
  async editStepAction() {
    if (!this.showButton) {
      this.loadStep();
    }
    this.focusOnFirstField = true;
  }

  /**
   * On click continue to next checkout step, after successful validation, render saved shipping address.
   * @returns 
   */
  renderCompletedStepResults(){
    const address = getShippingAddress(store.getState() as StoreState)!;
    return html`
      <address class="pb-3">
        <p class="mb-0">${address.firstName} ${address.lastName}</p>
        <p class="mb-0">${address.address1} ${address.address2}</p>
        <p class="mb-0">${address.city}, ${address.state} ${address.zipCode}</p>
      </address>
    `;
  }

  private getAddressForm() {
    return this.shadowRoot?.querySelector('mhecomm-address-form') as AddressForm | undefined;
  }

  renderLoadingTemplate() {
    return html`
      <span class="visually-hidden">Loading shipping address step</span>
      ${loading({ columns: 2 })}
      ${loading({ columns: 1, m: 'mt-4', width: '25%' })}
      ${loading({ columns: 1, m: 'mt-3' })}
      ${loading({ columns: 1, m: 'mt-4', width: '25%' })}
      ${loading({ columns: 1, m: 'mt-3' })}
      ${loading({ columns: 1, m: 'mt-4', width: '25%' })}
      ${loading({ columns: 1, m: 'mt-3' })}
      ${loading({ columns: 2, m: 'mt-3' })}
      ${loading({ columns: 1, m: 'mt-4', width: '25%' })}
      ${loading({ columns: 1, m: 'mt-3' })}
    `
  }

  checkoutStepCompleteDetails() {
    const state = store.getState() as StoreState;
    const cart = getCartForAnalytics(state);
    const cartAnalytics = {
      ...cart,
      products: getProductsWithAdditionalAnalytics({ products: cart.products ?? [] }),
    };

    return addAnalyticsCheckoutFlowMetadata({
      id: this.id,
      user: getUserDataForUserAnalytics(state),
      estimatedTax: getTax(state)?.totalTax.amount,
      promoCode: getDiscount(state),
      cart: cartAnalytics,
      comp: 'shipping-address-checkout-step',
      label: this.nextStepButtonName,
      url: window.location.href,
      ...(!!getPoOrderNumber(state) && isPurchaseOrderApplied(state) && 
      { 
        paymentType: "Purchase Order", 
        purchaseOrderNumber: getPoOrderNumber(state) 
      })
    })
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-shipping-address-checkout-step': ShippingAddressCheckoutStep;
  }
}
